import * as Sentry from '@sentry/gatsby';
import {
  isSameDay,
  startOfDay,
  isBefore,
  isAfter,
  addMonths,
  parseISO,
} from 'date-fns';
import { convertedTime } from './convertedTime';
import { ServiceLocation, removeTimePartFromDate } from '../../helpers';
import {
  AvailableService,
  AvailableServicesInZipResponse,
  AvailableStore,
  AvailableStoresResponse,
  AvailableTimeslotsResponse,
  LocationType,
  PriceWithSavings,
  Savings,
  DisplayPrice,
  Slot,
  TimeSlot,
  CustomerPrice,
  PriceOfferType,
  ReplacementService,
  TimeSlotsCapacity,
} from './model';
import {
  getFormattedPrice,
  isIkeaMembershipSavings,
} from '../../helpers/currencyHelper';
import { isBusinessOperation } from '../../helpers/urlHelper';
import { CustomerType } from '../../reducers/user/userTypes';

export const sortTimeSlots = (timeSlots: TimeSlot[]): TimeSlot[] => {
  return timeSlots.toSorted((a, b) => {
    const aDateFrom = new Date(a.dateFrom).getTime();
    const bDateFrom = new Date(b.dateFrom).getTime();
    if (aDateFrom === bDateFrom) {
      const aDateTo = new Date(a.dateTo).getTime();
      const bDateTo = new Date(b.dateTo).getTime();
      return aDateTo - bDateTo;
    }
    return aDateFrom - bDateFrom;
  });
};

const getLocation = (locationType: LocationType): ServiceLocation => {
  switch (locationType) {
    case 'STORE':
      return 'inStore';
    case 'STORE_REMOTE':
      return 'storeRemote';
    case 'HOME':
      return 'home';
    case 'STORE_HOME':
      return 'storeHome';
    default:
      return 'remote';
  }
};

const getExclSavingsPrice = (priceObject: PriceWithSavings): number => {
  return priceObject.price.priceInclTax || priceObject.price.priceExclTax || 0;
};

const getInclSavingsPrice = (priceObject: PriceWithSavings): number => {
  return (
    priceObject.price.priceInclTaxWithSavings ||
    priceObject.price.priceExclTaxWithSavings ||
    0
  );
};

type SavingsDateSpan = {
  validFromDateUTC?: string;
  validToDateUTC?: string;
};

const getValidDateSpan = (
  priceOfferType: PriceOfferType,
  savings?: Savings[],
): SavingsDateSpan => {
  if (!savings || savings.length === 0) {
    return {};
  }
  const dateSpan = savings.reduce((acc, saving): SavingsDateSpan => {
    const { validFromDateUTC, validToDateUTC } = acc;
    if (!validFromDateUTC) {
      acc.validFromDateUTC = saving.validFromDateUTC;
    } else if (isBefore(validFromDateUTC, saving.validFromDateUTC)) {
      acc.validFromDateUTC = saving.validFromDateUTC;
    }
    if (!validToDateUTC) {
      acc.validToDateUTC = saving.validToDateUTC;
    } else if (isAfter(validToDateUTC, saving.validToDateUTC)) {
      acc.validToDateUTC = saving.validToDateUTC;
    }
    return acc;
  }, {} as SavingsDateSpan);

  if (
    isIkeaMembershipSavings(priceOfferType) &&
    dateSpan.validToDateUTC &&
    isAfter(dateSpan.validToDateUTC, addMonths(Date.now(), 6))
  ) {
    return {};
  }

  return dateSpan;
};

const getPriceOfService = (
  regularPrice: number,
  currencyCode: string,
  appConfig: typeof CSS_CONFIG,
  priceWithSavings?: PriceWithSavings,
  priceOfferType?: PriceOfferType,
): DisplayPrice => {
  const inclSavings = priceWithSavings
    ? getInclSavingsPrice(priceWithSavings)
    : regularPrice;
  const { validFromDateUTC, validToDateUTC } = priceOfferType
    ? getValidDateSpan(priceOfferType, priceWithSavings?.savings)
    : {};
  return {
    currencyCode,
    exclSavings: regularPrice,
    exclSavingsFormatted: getFormattedPrice(
      regularPrice,
      appConfig,
      currencyCode,
    ),
    inclSavings,
    inclSavingsFormatted: getFormattedPrice(
      inclSavings,
      appConfig,
      currencyCode,
    ),
    totalSavings: priceWithSavings?.price.totalDiscount || 0,
    priceOfferType,
    validFromDateUTC,
    validToDateUTC,
  };
};

const getCustomerPrice = (
  customerType: CustomerType,
  allowedMemberDiscounts: boolean,
  isBusiness: boolean,
  regularPrice: number,
  currencyCode: string,
  appConfig: typeof CSS_CONFIG,
  familyPriceObject?: PriceWithSavings,
  businessPriceObject?: PriceWithSavings,
  guestPriceObject?: PriceWithSavings,
): CustomerPrice => {
  let price = regularPrice;
  let totalSavings: number | undefined;
  let priceOfferType: PriceOfferType | undefined;
  const troSavings = guestPriceObject?.price.totalDiscount || 0;
  if (
    !isBusiness &&
    customerType === 'Individual' &&
    familyPriceObject &&
    familyPriceObject.price.totalDiscount &&
    familyPriceObject.price.totalDiscount > troSavings
  ) {
    price = getInclSavingsPrice(familyPriceObject);
    totalSavings = familyPriceObject.price.totalDiscount;
    priceOfferType = 'family';
  } else if (
    customerType === 'Business' &&
    allowedMemberDiscounts &&
    businessPriceObject &&
    businessPriceObject.price.totalDiscount &&
    businessPriceObject.price.totalDiscount > troSavings
  ) {
    price = getInclSavingsPrice(businessPriceObject);
    totalSavings = businessPriceObject.price.totalDiscount;
    priceOfferType = 'ibn';
  } else if (guestPriceObject && guestPriceObject?.price.totalDiscount) {
    price = getInclSavingsPrice(guestPriceObject);
    totalSavings = guestPriceObject.price.totalDiscount;
    priceOfferType = 'tro';
  }
  return {
    price,
    priceFormatted: getFormattedPrice(price, appConfig, currencyCode),
    totalSavings,
    priceOfferType,
  };
};

const getServicePrice = (
  prices: PriceWithSavings[],
  operation: string,
  customerType: CustomerType,
  allowedMemberDiscounts: boolean,
  appConfig: typeof CSS_CONFIG,
): { displayPrice: DisplayPrice; customerPrice: CustomerPrice } => {
  const guestPriceObject = prices.find(
    (priceObj) => priceObj.price.priceType === 'Private',
  );
  const familyPriceObject = prices.find(
    (priceObj) => priceObj.price.priceType === 'Family',
  );
  const businessPriceObject = prices.find(
    (priceObj) => priceObj.price.priceType === 'Business',
  );
  const availablePriceObject =
    guestPriceObject || familyPriceObject || businessPriceObject;
  if (!availablePriceObject) {
    Sentry.captureException('Missing price object for service');
    throw new Error('Missing price object for service');
  }

  const isFamilyPriceAllowed =
    !isBusinessOperation(operation, appConfig) && customerType !== 'Business';
  const isBusinessPriceAllowed =
    isBusinessOperation(operation, appConfig) || customerType !== 'Individual';
  const regularPrice = getExclSavingsPrice(availablePriceObject);
  const currencyCode = availablePriceObject.price.currencyCode;
  const familySavings = familyPriceObject?.price.totalDiscount || 0;
  const businessSavings = businessPriceObject?.price.totalDiscount || 0;
  const guestSavings = guestPriceObject?.price.totalDiscount || 0;

  let priceObjectWithSavings: PriceWithSavings | undefined;
  let priceOfferType: PriceOfferType | undefined;
  if (isFamilyPriceAllowed && familySavings > guestSavings) {
    priceObjectWithSavings = familyPriceObject;
    priceOfferType = 'family';
  } else if (isBusinessPriceAllowed && businessSavings > guestSavings) {
    priceObjectWithSavings = businessPriceObject;
    priceOfferType = 'ibn';
  } else if (guestSavings > 0) {
    priceObjectWithSavings = guestPriceObject;
    priceOfferType = 'tro';
  }
  return {
    displayPrice: getPriceOfService(
      regularPrice,
      currencyCode,
      appConfig,
      priceObjectWithSavings,
      priceOfferType,
    ),
    customerPrice: getCustomerPrice(
      customerType,
      allowedMemberDiscounts,
      isBusinessOperation(operation, appConfig),
      regularPrice,
      currencyCode,
      appConfig,
      familyPriceObject,
      businessPriceObject,
      guestPriceObject,
    ),
  };
};

export const toAvailableService =
  (
    operation: string,
    customerType: CustomerType,
    allowedMemberDiscounts: boolean,
    appConfig: typeof CSS_CONFIG,
  ) =>
  (service: AvailableServicesInZipResponse): AvailableService => {
    const {
      serviceProductId,
      serviceCode,
      locationType,
      prices,
      replacementService,
      isExternalService,
    } = service;
    const { displayPrice, customerPrice } = getServicePrice(
      prices,
      operation,
      customerType,
      allowedMemberDiscounts,
      appConfig,
    );

    let toReplacementService: ReplacementService | undefined;
    if (replacementService) {
      const {
        displayPrice: replacementServiceDisplayPrice,
        customerPrice: replacementServiceCustomerPrice,
      } = getServicePrice(
        replacementService.prices || [],
        replacementService.operation,
        customerType,
        allowedMemberDiscounts,
        appConfig,
      );
      toReplacementService = {
        serviceId: replacementService.serviceProductId,
        serviceCode: replacementService.serviceCode,
        operation: replacementService.operation,
        location: getLocation(replacementService.locationType),
        displayPrice: replacementServiceDisplayPrice,
        customerPrice: replacementServiceCustomerPrice,
        isExternalService: replacementService.isExternalService,
      };
    }

    return {
      serviceId: serviceProductId,
      serviceCode,
      location: getLocation(locationType),
      displayPrice,
      customerPrice,
      replacementService: toReplacementService,
      isExternalService,
    };
  };

export const sortServicesByPrice = (
  serviceA: AvailableService,
  serviceB: AvailableService,
): number => {
  return serviceA.displayPrice.exclSavings - serviceB.displayPrice.exclSavings;
};

export const toAvailableStore = (
  store: AvailableStoresResponse,
): AvailableStore => {
  return {
    value: store.externalId,
    text: store.name,
    street: store.street,
    zipCode: store.zipCode,
    city: store.city,
  };
};

const toTimeSlot = (
  slot: AvailableTimeslotsResponse,
  isTimeInBrowserSettings: boolean,
  appConfig: typeof CSS_CONFIG,
  isReplacementService?: boolean,
): TimeSlot => {
  const { from, to, id, externalId } = slot;
  return {
    from: convertedTime(from, isTimeInBrowserSettings, appConfig),
    to: convertedTime(to, isTimeInBrowserSettings, appConfig),
    dateFrom: from,
    dateTo: to,
    id,
    appointmentId: externalId,
    isReplacementService,
  };
};

/**
 * External services might return multiple time slots with the same time range.
 * This function removes those duplicates by keeping one of them randomly.
 */
export const removeDuplicateTimeSlotRanges = (slots: Slot[]) => {
  return slots.map((slot) => {
    const timeSlotsByTimeRange = slot.timeSlots.reduce((map, timeSlot) => {
      const timeRange = `${timeSlot.dateFrom}-${timeSlot.dateTo}`;
      if (!map.has(timeRange)) {
        map.set(timeRange, timeSlot);
      } else {
        const shouldReplace = Math.random() < 0.5;
        if (shouldReplace) {
          map.set(timeRange, timeSlot);
        }
      }
      return map;
    }, new Map<string, TimeSlot>());

    const filteredTimeSlots = Array.from(timeSlotsByTimeRange.values());
    return { ...slot, timeSlots: filteredTimeSlots };
  });
};

export const toSlots = (
  slotsPayload: AvailableTimeslotsResponse[],
  isTimeInBrowserSettings: boolean,
  firstDateToShow: string,
  capacities: TimeSlotsCapacity[],
  flagLastCompleteSlotAsPending: boolean,
  appConfig: typeof CSS_CONFIG,
  isReplacementService?: boolean,
): Slot[] => {
  const slots = slotsPayload
    .reduce((acc: Slot[], availableTimeSlot: AvailableTimeslotsResponse) => {
      const dateWithoutTime = removeTimePartFromDate(availableTimeSlot.from);
      const isDateOutsideCalendar = isTimeInBrowserSettings
        ? isBefore(availableTimeSlot.from, firstDateToShow)
        : isBefore(dateWithoutTime, removeTimePartFromDate(firstDateToShow));

      if (isDateOutsideCalendar) {
        return acc;
      }

      const existingSlotIndex = acc.findIndex((existingSlot) => {
        const dateToCheck = isTimeInBrowserSettings
          ? availableTimeSlot.from
          : parseISO(dateWithoutTime).toISOString();
        return isSameDay(dateToCheck, existingSlot.date);
      });

      if (existingSlotIndex === -1) {
        const timeSlot = toTimeSlot(
          availableTimeSlot,
          isTimeInBrowserSettings,
          appConfig,
          isReplacementService,
        );
        const timeSlotDay = isTimeInBrowserSettings
          ? startOfDay(timeSlot.dateFrom).toISOString()
          : parseISO(dateWithoutTime).toISOString();
        acc.push({
          date: timeSlotDay,
          timeSlots: [timeSlot],
          status: 'completed',
        });
        return acc;
      }

      acc[existingSlotIndex].timeSlots.push(
        toTimeSlot(
          availableTimeSlot,
          isTimeInBrowserSettings,
          appConfig,
          isReplacementService,
        ),
      );
      return acc;
    }, [])
    .toSorted(
      ({ date: dateA }, { date: dateB }) =>
        new Date(dateA).getTime() - new Date(dateB).getTime(),
    );

  if (flagLastCompleteSlotAsPending && slots.length > 1) {
    slots[slots.length - 1].status = 'pending';
  }

  if (capacities.length) {
    capacities
      .toSorted(
        ({ date: dateA }, { date: dateB }) =>
          new Date(dateA).getTime() - new Date(dateB).getTime(),
      )
      .forEach((capacity) => {
        const capacityDate = parseISO(capacity.date).toISOString();
        const slotIndex = slots.findIndex((slot) => slot.date === capacityDate);
        const isSlotMissing = !slots[slotIndex];
        if (isSlotMissing) {
          slots.push({
            date: capacityDate,
            timeSlots: [],
            status: 'pending',
          });
        }
      });
  }

  const slotsWithUniqueTimeSlotRanges = removeDuplicateTimeSlotRanges(slots);
  return slotsWithUniqueTimeSlotRanges.map(
    (slot: Slot): Slot => ({
      ...slot,
      timeSlots: sortTimeSlots(slot.timeSlots),
    }),
  );
};

export const markSelectedSlotAsCompleted = (
  slots: Slot[],
  pendingSelectedSlot: Slot,
) => {
  const indexOfSelectedSlotStillPending = slots.findIndex(
    (slot) =>
      slot.date === pendingSelectedSlot.date && slot.status === 'pending',
  );
  if (indexOfSelectedSlotStillPending !== -1) {
    slots[indexOfSelectedSlotStillPending] = {
      ...slots[indexOfSelectedSlotStillPending],
      status: 'completed',
    };
  }
};
